import { useState, useRef } from 'react'
import type { WizardState } from './types'
import { SectionBlock, FieldRow, inputStyle } from './ui'
import { apiFetchRaw } from '../../../lib/api-client'

const BLUE = '#4f46e5'

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconDoc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </svg>
)
const IconLeaf = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
)
const IconPaperclip = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
)
const IconInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
)
const IconHash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
  </svg>
)
const IconUpload = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

// ─── Standard husorden text ───────────────────────────────────────────────────
const STANDARD_HUSORDEN = `§1 Støj og ro-tider
Lejerne skal vise hensyn over for hinanden og undgå støjende adfærd. Ro-tider er hverdage kl. 22:00-07:00 samt søn- og helligdage kl. 22:00-09:00. Brug af støjende apparater, musik og lignende skal begrænses til dagtimerne.

§2 Affald
Affald skal sorteres og afleveres i de dertil beregnede beholdere. Storskrald og haveaffald må kun henlægges, hvor og når det er anvist af udlejeren. Det er ikke tilladt at henstille storskrald i opgange, kældre eller på fællesarealer.

§3 Rygning
Rygning er ikke tilladt i opgange, kældre og øvrige fællesarealer. Rygning i lejemålet er reguleret af lejekontraktens særlige vilkår.

§4 Husdyr
Husdyr er kun tilladt efter lejekontraktens særlige vilkår og udlejerens samtykke. Husdyrholdet må ikke være til gene for ejendommens øvrige beboere. Eventuelle skader forvoldt af husdyr er lejerens ansvar.

§5 Fællesarealer
Gangarealer, trapper og kældre må ikke benyttes til opbevaring. Barnevogne og cykler må kun henstilles i anviste rum. Fællesarealer skal holdes ryddelige og må ikke benyttes til opmagasinering.

§6 Parkering
Parkering er kun tilladt i anviste parkeringspladser eller -rum. Uautoriseret parkering kan medføre fjernelse af køretøjet for ejerens regning. Henstilling af uindregistrerede køretøjer er ikke tilladt.

§7 Vaskeri
Fællesvaskeriet benyttes i henhold til den til enhver tid gældende vaske- og bestillingsplan. Vaskeriet efterlades i ren og ryddelig stand efter brug. Beskadigelse af maskiner skal straks meddeles udlejeren.

§8 Post og navneskilte
Lejeren sørger selv for at sætte navn på postkassen. Post til fraflyttede lejere må ikke videresendes uden udlejerens samtykke.

§9 Brug af lejemålet
Lejemålet må kun benyttes til beboelse. Erhvervsmæssig virksomhed kræver udlejerens skriftlige samtykke. Fremleje og korttidsudlejning (f.eks. Airbnb) er ikke tilladt uden særskilt aftale.

§10 Istandsættelse og vedligeholdelse
Lejeren er forpligtet til at behandle lejemålets indretning med omhu og foretage den daglige rengøring. Skader og mangler skal straks meddeles udlejeren. Lejeren er ansvarlig for skader, der skyldes hensynsløs adfærd eller forsømmelse af vedligeholdelsespligten.`

// ─── Radio option ─────────────────────────────────────────────────────────────
function RadioOpt({ label, description, value, current, onChange }: {
  label: string; description?: string; value: string; current: string; onChange: (v: string) => void
}) {
  const active = current === value
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
      padding: '10px 12px', borderRadius: 7, border: `1px solid ${active ? BLUE : '#d1d5db'}`,
      background: active ? '#eef2ff' : '#fff', marginBottom: 5,
    }}>
      <input type="radio" name="husorden" value={value} checked={active} onChange={() => onChange(value)} style={{ accentColor: BLUE, marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13, color: '#374151', fontWeight: active ? 500 : 400 }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{description}</div>}
      </div>
    </label>
  )
}

// ─── Standard husorden modal ───────────────────────────────────────────────────
function HusordenModal({ onClose }: { onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 600, width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>Standard husorden fra Boligflow</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', padding: 0 }}>x</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', fontSize: 13, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
          {STANDARD_HUSORDEN}
        </div>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: BLUE, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Luk</button>
        </div>
      </div>
    </div>
  )
}

// ─── File upload zone ─────────────────────────────────────────────────────────
function PdfUploadZone({ label, onFile }: { label: string; onFile: (name: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const handleFile = (file: File) => {
    setFileName(file.name)
    onFile(file.name)
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? BLUE : '#d1d5db'}`,
        borderRadius: 10, padding: '20px 24px', textAlign: 'center',
        background: dragging ? '#eff6ff' : '#fafafa', color: '#9ca3af', fontSize: 13,
        cursor: 'pointer', transition: 'border-color .15s, background .15s',
        marginBottom: 6,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
      />
      {fileName ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#374151' }}>
          <IconDoc />
          <span style={{ fontWeight: 500 }}>{fileName}</span>
          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>✓ Uploadet</span>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}><IconUpload /></div>
          <div style={{ marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 12 }}>Træk PDF hertil eller klik for at vælge fil</div>
        </>
      )}
    </div>
  )
}

interface BilagItem {
  id: string
  filename: string
  file_size: number
}

// ─── Main component ───────────────────────────────────────────────────────────
export function SectionOvrigt({ state, onChange, draftId, saveDraft }: {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
  draftId: string | null
  saveDraft: (state: WizardState) => Promise<string>
}) {
  const [showHusordenModal, setShowHusordenModal] = useState(false)
  const [bilag, setBilag] = useState<BilagItem[]>([])
  const [uploading, setUploading] = useState(false)
  const bilagInputRef = useRef<HTMLInputElement>(null)

  async function downloadHusordenPdf() {
    // Use apiFetchRaw: absolute API URL (no Vite-proxy dependency that breaks in
    // prod) + correct token + refresh. Relative fetch returned index.html on Netlify.
    const r = await apiFetchRaw('/api/v1/contracts/husorden/standard-pdf')
    if (!r.ok) return
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'husorden-boligflow.pdf'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function handleBilagUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Kun PDF-filer er understøttet')
      e.target.value = ''
      return
    }

    setUploading(true)
    try {
      // Ensure a draft exists before uploading
      const contractId = draftId ?? await saveDraft(state)

      const formData = new FormData()
      formData.append('file', file)

      const r = await apiFetchRaw(`/api/v1/contracts/${contractId}/bilag`, {
        method: 'POST',
        body: formData,
      })
      if (!r.ok) {
        alert('Upload fejlede')
        return
      }
      const newBilag = await r.json()
      setBilag(prev => [...prev, newBilag])
    } catch {
      alert('Upload fejlede')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function handleDeleteBilag(id: string) {
    const contractId = draftId
    if (!contractId) return
    await apiFetchRaw(`/api/v1/contracts/${contractId}/bilag/${id}`, { method: 'DELETE' })
    setBilag(prev => prev.filter(b => b.id !== id))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {showHusordenModal && <HusordenModal onClose={() => setShowHusordenModal(false)} />}

      {/* ── Husorden ─────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconDoc />} title="Husorden" subtitle="Findes der en husorden for ejendommen?">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Skal der tilføjes en husorden som bilag?</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>Påkrævet</span>
        </div>

        <RadioOpt label="Nej, ingen husorden" value="nej" current={state.husorden} onChange={v => onChange({ husorden: v as WizardState['husorden'] })} />
        <RadioOpt
          label="Ja, brug standard husorden fra Boligflow"
          description="Boligflows standard husorden med 10 paragraffer om ro, affald, rygning m.m."
          value="standard"
          current={state.husorden}
          onChange={v => onChange({ husorden: v as WizardState['husorden'] })}
        />
        {state.husorden === 'standard' && (
          <div style={{ marginBottom: 6, paddingLeft: 4, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setShowHusordenModal(true)}
              style={{ fontSize: 12, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              Se standard husorden
            </button>
            <button
              type="button"
              onClick={downloadHusordenPdf}
              style={{
                fontSize: 12, color: '#374151', background: 'none',
                border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
              }}
            >
              ↓ Hent PDF
            </button>
          </div>
        )}
        <RadioOpt
          label="Ja, vedhæft husorden fra ejendom"
          description="Henter husorden tilknyttet ejendommen (konfigureres under Ejendomme)"
          value="ejendom"
          current={state.husorden}
          onChange={v => onChange({ husorden: v as WizardState['husorden'] })}
        />
        {state.husorden === 'ejendom' && (
          <div style={{ marginBottom: 6, padding: '8px 12px', borderRadius: 8, background: '#f9fafb', border: '1px solid #e5e7eb', fontSize: 12, color: '#9ca3af' }}>
            Ejendommen har ingen husorden tilknyttet endnu. Tilknyt en husorden under Ejendomme.
          </div>
        )}
        <RadioOpt label="Ja, vedhæft egen husorden" description="Upload en PDF-fil" value="ja" current={state.husorden} onChange={v => onChange({ husorden: v as WizardState['husorden'] })} />
        {state.husorden === 'ja' && (
          <div style={{ marginBottom: 6 }}>
            <PdfUploadZone label="Upload husorden som PDF" onFile={() => {}} />
          </div>
        )}
      </SectionBlock>

      {/* ── Energimærke ──────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconLeaf />} title="Energimærke" subtitle="Findes der en energimærkerapport for ejendommen?">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 8, border: '1px solid #e8eaed', background: '#f7f8fa' }}>
          <div style={{ flexShrink: 0, color: '#9ca3af' }}>
            <IconUpload />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Hent energimærke</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Hentes fra Energistyrelsen via BBR-adressen, hvis tilgængeligt</div>
          </div>
          <button
            type="button"
            style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: BLUE, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}
            onClick={() => {/* Phase 2: fetch from Energistyrelsen API */}}
          >
            Hent
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>
            Eller upload manuelt
          </div>
          <PdfUploadZone label="Upload energimærkerapport som PDF" onFile={() => {}} />
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
          Automatisk hentning via Energistyrelsen API aktiveres i næste version.
        </div>
      </SectionBlock>

      {/* ── Andre bilag ──────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconPaperclip />} title="Andre bilag" subtitle="Er der andre bilag der skal vedlægges?">
        <div
          style={{ border: '2px dashed #d1d5db', borderRadius: 10, padding: '32px 24px', textAlign: 'center', background: '#fafafa', color: '#9ca3af', fontSize: 13 }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault()
            if (uploading) return
            const file = e.dataTransfer.files[0]
            if (file && bilagInputRef.current) {
              const dt = new DataTransfer()
              dt.items.add(file)
              bilagInputRef.current.files = dt.files
              bilagInputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
            }
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <IconUpload />
          </div>
          <div style={{ marginBottom: 4 }}>{uploading ? 'Uploader…' : 'Træk filen herind eller vælg en fil'}</div>
          <div style={{ fontSize: 12 }}>Understøtter PDF-dokumenter</div>
          <input
            ref={bilagInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            id="bilag-upload"
            onChange={handleBilagUpload}
          />
          <label
            htmlFor="bilag-upload"
            style={{
              display: 'inline-block', marginTop: 12, padding: '6px 16px', borderRadius: 8,
              border: '1px solid #d1d5db', background: uploading ? '#f3f4f6' : '#fff',
              color: uploading ? '#9ca3af' : '#374151', fontSize: 12,
              cursor: uploading ? 'not-allowed' : 'pointer',
              pointerEvents: uploading ? 'none' : 'auto',
            }}
          >
            {uploading ? 'Uploader…' : 'Vælg fil'}
          </label>
        </div>

        {bilag.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bilag.map(b => (
              <div
                key={b.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff',
                }}
              >
                <IconDoc />
                <span style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.filename}</span>
                <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{(b.file_size / 1024).toFixed(0)} KB</span>
                <button
                  type="button"
                  onClick={() => handleDeleteBilag(b.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4, display: 'flex', alignItems: 'center' }}
                  title="Slet bilag"
                >
                  <IconTrash />
                </button>
              </div>
            ))}
          </div>
        )}
      </SectionBlock>

      {/* ── Øvrige oplysninger §10 ────────────────────────────────────────── */}
      <SectionBlock icon={<IconInfo />} title="Øvrige oplysninger §10" subtitle="Er der øvrige oplysninger om det lejede?">
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, lineHeight: 1.55 }}>
          Her må der ikke tilføjes vilkår, som allerede følger af lejelovens almindelige regler — de skal anføres under Særlige vilkår §11.
        </div>
        <FieldRow label="Øvrige oplysninger om det lejede">
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' } as React.CSSProperties}
            value={state.other_notes}
            onChange={e => onChange({ other_notes: e.target.value })}
            placeholder="Øvrige oplysninger..."
          />
        </FieldRow>
      </SectionBlock>

      {/* ── Lejemålsnummer ────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconHash />} title="Lejemålsnummer" subtitle="Internt referencenummer for lejemålet">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#374151', flexShrink: 0 }}>Nr.</span>
          <input
            style={{ ...inputStyle, width: 160 }}
            type="text"
            placeholder="1234"
            value={state.unit_number}
            onChange={e => onChange({ unit_number: e.target.value })}
          />
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Valgfrit</span>
        </div>
        {state.prefilled_fields.includes('unit_number') && (
          <div style={{ fontSize: 11, color: '#059669', marginTop: 6 }}>
            ✓ Forududfyldt fra lejemål
          </div>
        )}
      </SectionBlock>

    </div>
  )
}
